import React, { useState, useEffect, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Layout, Modal, Tag, Typography, message } from 'antd';
import intl from 'react-intl-universal';
import moment from 'moment';
import { debounce } from 'lodash';
import { SettingOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons';

import { storage } from '@/utils';
import { getCurrentEmployee } from '@/redux/actions/user';
import { queryAllMemberLevelList } from '@/redux/actions/guest';
import RightContextMenu from '@/components/ContextMenu/index';
import ContextMisc from '@/components/ContextMisc';
import IntlSettingModal, { IntlSettingModalRef } from '@/components/Intl/IntlSettingModal';
import IconFont from '@/components/IconFont';
import { IdrawerRef } from '@/components/CustomDrawer';
import ShopSwitcher, { ImodalRef } from '@/pages/login/components/ChooseShop';
import HandoverDrawer from '@/pages/handover';
import ForceHandover from '@/pages/handover/force';
import ReadDoorCardModal from '@/components/Cards/components/ReadDoorCardModal';
import ReadCardModal from '@/components/Cards/read';
import InvitationDetailDrawer from '@/pages/order/crsInvitation/InvitationDetailDrawer';
import { logout } from '@/redux/actions/login';
import { fetchOnDuty } from '@/api/handover';
import IntlContext from '@/components/Intl/IntlContext';
import { getTimeByUtcOffset, TenantSignStorage } from '@/utils/intl';
import { logoutToLogin } from '@/utils/user';
import { intiTimeZoneStr } from '@/utils/formatDate';
import { useShortcuts } from '@/hooks/useShortcuts';
import { DoorLockCode } from '@/pages/device/card/interface';
import useReservePortal from '@/pages/reservation/reserve';
import useCheckInPortal from '@/pages/reservation/checkIn/checkIn';
import CardPurchase, { IcardPurchaseRef } from '@/components/CardPurchase';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';

import RouteList from './routeList';
import Aside from './aside';
import Header from './header';
import RealTimeClock from './RealTimeClock';
import SystermNotice from './SystermNotice';

import styles from './index.module.scss';

// 添加 PmsHeaderRef 接口导入
import type { PmsHeaderRef } from './header';

const { Content } = Layout;
const { Text } = Typography;

function PmsLayout(props) {
  const { userInfo, currentShop, shops, companyGroup, enabledVendor } = props;
  const { name: userName, postName } = userInfo || {};

  const isGroupLogin = storage.get('isGroupLogin');
  const history = useHistory();
  const reservePortal = useReservePortal(); // 新入住
  const checkInPortal = useCheckInPortal(); // 新预定

  const [forceModalVisible, setForceModalVisible] = useState(false);
  const [isHandover, setIsHandover] = useState(false);

  const switchModalRef = useRef<ImodalRef>(null);
  const handoverRef = useRef<IdrawerRef>();
  const intlModalRef = useRef<IntlSettingModalRef>();
  const intlCtx = useContext(IntlContext);
  // 商户名称
  const merchantName = TenantSignStorage.getLocalConfig()?.marchantName || '--';
  const readRef = useRef<any>();
  const invitationDetailRef = useRef<any>();
  const [readModalVisible, setReadModalVisible] = useState(false);
  const cardPurchaseRef = useRef<IcardPurchaseRef>();
  const headerRef = useRef<PmsHeaderRef>();
  // // 初始化 send service
  // const { sendServive } = useSendShopInfo();

  const checkHandover = async () => {
    try {
      const res = await fetchOnDuty();
      if (res && res.status) {
        setForceModalVisible(!res.data);

        const handoverUserId = res.data;
        if (handoverUserId && userInfo) {
          setIsHandover(handoverUserId === (userInfo.employeeNo || userInfo.userId));
        }
      }
    } catch (e) {
      setForceModalVisible(false);
    }
  };

  // check login
  const checkLogin = () => {
    const token = storage.get('token');
    if (token) {
      // 查询所有会员等级
      props.queryAllMemberLevelList();
      props
        .getCurrentEmployee()
        .then(({ shopsList }) => {
          // 是否是集团登录
          if (isGroupLogin) return;
          // 若本地缓存的commonCode不在用户当前所属的门店，重新登录
          const commonCode = storage.get('commonCode');
          if (shopsList?.every((item) => commonCode !== item.commonCode)) {
            message.warning(intl.get('layout_warning_no_shop'));
            setTimeout(() => {
              logoutToLogin();
            }, 1000);
          }
          if (commonCode) {
            checkHandover();
          }
        })
        .catch(() => {
          logoutToLogin();
        });
    } else {
      logoutToLogin();
    }
  };

  const signOut = () => {
    props.logout().then(() => {
      logoutToLogin();
    });
  };

  const handleLogout = () => {
    Modal.confirm({
      title: intl.get('app_logout'),
      content: intl.get('app_logout_content'),
      okText: intl.get('app_confirm_verify_btn'),
      onOk() {
        signOut();
      },
    });
  };

  // 国际化配置
  const showIntlSettingModal = (e) => {
    e.stopPropagation();
    // 原来被注释掉的代码
    // intlModalRef.current.showModal();

    // 调用 headerRef 的 openQuickEntry 方法
    if (headerRef.current) {
      headerRef.current.openQuickEntry();
    } else {
      console.log('headerRef.current is undefined');
    }
  };

  // 投诉举报
  const goComplaintPage = () => {
    history.push('/help/complaint');
  };

  useEffect(() => {
    checkLogin();
    intlCtx.initTenantConfig({ isUserLogin: true });
    // // 发送commonCode 到c++
    // if (!storage.get('isGroupLogin')) sendServive(storage.get('commonCode'));
  }, []);

  // 2024-06-19 解决antd Table sticky 出现两个滚动条的 bug(滚动条1：sticky bar 滚动条2：scroll={{x:'max-content}})
  const handleScroll = () => {
    const e = new Event('resize');
    window.dispatchEvent(e);
  };

  const handleRoomCard = () => {
    if (enabledVendor?.lockCode === DoorLockCode.BE_LINE) {
      readRef.current.openModal();
    } else {
      setReadModalVisible(true);
    }
  };

  const handleNewCard = () => {
    cardPurchaseRef?.current?.openDrawer({
      isNew: true,
      certType: TenantGlobalConfig.getSystemConfig().defaultCertType,
    });
  };

  const handleInvite = () => {
    invitationDetailRef?.current.openDrawer({});
  };
  const handleHandover = () => {
    handoverRef?.current?.openDrawer();
  };
  const handleReservePortal = () => {
    reservePortal.showDrawer();
  };
  const handleCheckInPortal = () => {
    checkInPortal.showDrawer();
  };
  // 使用快捷键
  useShortcuts({
    handleRoomCard,
    handleNewCard,
    handleInvite,
    handleHandover,
    handleReservePortal,
    handleCheckInPortal,
  });
  return (
    <>
      <ShopSwitcher ref={switchModalRef} />
      <ForceHandover visible={forceModalVisible} />
      <HandoverDrawer ref={handoverRef} handoverCallback={() => checkHandover()} />

      <Layout className={styles.layoutContainer}>
        <Header
          ref={headerRef}
          handleNewCard={handleNewCard}
          handleInvite={handleInvite}
          handleHandover={handleHandover}
          handleReservePortal={handleReservePortal}
          handleCheckInPortal={handleCheckInPortal}
        />
        <Layout className={styles.layoutMain}>
          <Aside />
          <Content className={styles.layoutContent} onScroll={debounce(handleScroll, 100)}>
            <div className={styles.layoutContentWrapper}>
              <RouteList />
            </div>
          </Content>
        </Layout>
        <SystermNotice />
        <div className={styles.bottomStatusBar}>
          <div>
            {/* 门店信息 */}
            {currentShop ? (
              <Text ellipsis={{ tooltip: currentShop?.shopName }}>
                {storage.get('commonCode')}
                {currentShop?.shopName}
              </Text>
            ) : null}
            {/* 集团信息 */}
            {!currentShop && userName ? (
              <Text ellipsis={{ tooltip: merchantName }}>{merchantName}</Text>
            ) : null}

            {/* 切换门店按钮 */}
            {/* 拥有集团权限一直展示切换门店按钮 */}
            {companyGroup || (shops && shops.length > 1) ? (
              <div
                onClick={() =>
                  switchModalRef.current.openModal({
                    defaultShop: storage.get('commonCode'),
                  })
                }
              >
                <Button className={styles.switchStore}>
                  <IconFont type="icon-qiehuanmendian" /> {intl.get('app_switch_store')}
                </Button>
              </div>
            ) : null}

            {/* 登录人信息 */}
            <Text className={styles.userName} ellipsis={{ tooltip: `${userName}-${postName}` }}>
              {userName} - {postName}
            </Text>
            {isHandover && (
              <Tag
                color="#87d068"
                style={{
                  marginLeft: '8px',
                  height: 16,
                  fontSize: 12,
                  lineHeight: 1.1,
                  backgroundColor: '#64DC99',
                }}
              >
                {intl.get('app_on_duty')}
              </Tag>
            )}

            {/* 集团登录不需要展示交班按钮 */}
            {!isGroupLogin ? (
              <>
                <Button
                  className={styles.handover}
                  onClick={() => handoverRef.current?.openDrawer()}
                >
                  <SyncOutlined /> {intl.get('app_exchange_shift')}
                </Button>
              </>
            ) : null}

            {/* 登出 */}
            <div className={styles.logout} onClick={handleLogout}>
              <UploadOutlined style={{ marginRight: '4px' }} />
              {intl.get('app_logout')}
            </div>
          </div>

          <div>
            <div className={styles.date}>
              <span style={{ display: 'inline-block', width: 164 }}>
                <RealTimeClock />
              </span>
              <span style={{ marginLeft: '8px' }}>{intiTimeZoneStr(getTimeByUtcOffset())}</span>
            </div>
            <div className={styles.setting} onClick={showIntlSettingModal}>
              <SettingOutlined style={{ marginRight: '2px' }} />
              {intl.get('zoom_label')}
            </div>
            <div className={styles.logout} onClick={goComplaintPage}>
              <IconFont type="icon-tousujubao" style={{ marginRight: '2px' }} />
              {intl.get('layout_tou_su_ju_bao')}
            </div>
          </div>
        </div>
        <RightContextMenu />
        <ContextMisc />
        <IntlSettingModal type="cloud" ref={intlModalRef} />
        {!isGroupLogin && (
          <>
            {enabledVendor?.lockCode === DoorLockCode.BE_LINE ? (
              <ReadDoorCardModal
                innerRef={readRef}
                enabledVendor={enabledVendor}
                readCallBack={() => readRef.current.closeModal()}
              />
            ) : (
              <ReadCardModal
                readModalVisible={readModalVisible}
                readCallBack={() => {
                  setReadModalVisible(false);
                }}
              />
            )}
            <CardPurchase ref={cardPurchaseRef} />
            <InvitationDetailDrawer ref={invitationDetailRef} />
          </>
        )}
      </Layout>
    </>
  );
}

const mapStateToProps = (state) => {
  const { userInfo, currentShop, shops } = state.user;
  const { enabledVendor } = state.card;
  return { userInfo, currentShop, shops, enabledVendor };
};

export default connect(mapStateToProps, {
  logout,
  getCurrentEmployee,
  queryAllMemberLevelList,
})(PmsLayout);
